/**
 * Feature to translation key mappings
 * Organized by categories for better maintainability
 */

import type { FeatureMapping } from './featuresFormater.types';

/**
 * Centralized mapping of feature identifiers to i18n translation keys
 *
 * Categories:
 * - Identity Providers (IDP)
 * - Network Features
 * - Agent & Platform Configuration
 * - Security Features
 * - SIEM Integration
 * - Two-Factor Authentication
 * - Protocols & Applications
 * - Platform Features
 * - Category Labels
 */
export const FEATURE_TRANSLATION_MAP: FeatureMapping = {
  // ========================================
  // Identity Providers (IDP)
  // ========================================
  gsuiteEnabled: 'activity:customers.G_SUITE',
  azureEnabled: 'activity:customers.AZURE',
  oktaEnabled: 'activity:customers.OKTA',
  adldapEnabled: 'activity:customers.LDAP',
  databaseEnabled: 'activity:customers.DATABASE',

  // ========================================
  // Network Features
  // ========================================
  usingDnsfiltering: 'activity:platform.DNS_FILTERING',
  isUsingTunnels: 'activity:platform.TUNNELS',
  splitTunnel: 'activity:platform.SPLIT_TUNNELING',
  customDnsEnabled: 'activity:platform.CUSTOM_DNS',
  allowAccessPublicLocation: 'activity:customers.PUBLIC_VPN_LOCATIONS',
  publicVpnNetworks: 'activity:customers.PUBLIC_VPN_NETWORKS',

  // ========================================
  // Agent & Platform Configuration
  // ========================================
  // WiFi Security
  autoWifisecEnabled: 'activity:customers.AUTOMATIC_WIFI_SECURITY',
  autoWifisec: 'activity:customers.AUTOMATIC_WIFI_SECURITY', // Alias

  // Kill Switch
  enablekillswitch: 'activity:customers.KILL_SWITCH',
  killSwitch: 'activity:customers.KILL_SWITCH', // Alias

  // Application Updates
  upgradeApplicationEnabled: 'activity:customers.UPGRADE_APPLICATION',
  forceUpdate: 'activity:customers.UPGRADE_APPLICATION', // Alias
  automaticSilentUpdate: 'activity:customers.SILENT_UPDATE',
  disableUpdate: 'activity:customers.DISABLE_UPDATE',

  // Connection & Behavior
  vpnAlwaysOn: 'activity:customers.VPN_ALWAYS_ON',
  alwaysOnEnabled: 'activity:customers.DISABLE_SIGN_OUT',
  connectOnLaunch: 'activity:customers.CONNECT_ON_LAUNCH',
  connectDisconnectNotification:
    'activity:customers.CONNECT_DISCONNECT_NOTIFICATION',
  startMinimized: 'activity:customers.START_MINIMIZED',
  autoLogout: 'activity:customers.AUTO_LOGOUT',
  notifyReconnect: 'activity:customers.NOTIFY_RECONNECT',

  // Network Configuration
  trustedWifiNetworks: 'activity:customers.TRUSTED_WIFI_NETWORKS',
  dnsLeak: 'activity:customers.DNS_LEAK',

  // Monitoring & Reporting
  snowplowReport: 'activity:customers.SNOWPLOW_REPORT',
  crashReports: 'activity:customers.CRASH_REPORT',

  // ========================================
  // Security Features
  // ========================================
  policies: 'activity:platform.POLICIES',

  // ========================================
  // SIEM Integration
  // ========================================
  splunk: 'activity:customers.SPLUNK',
  amazons3: 'activity:customers.AMAZON_S3',
  azureSentinel: 'activity:customers.AZURE_SENTINEL',

  // ========================================
  // Two-Factor Authentication
  // ========================================
  'authenticator:sms': 'activity:customers.SMS',
  'authenticator:google-authenticator':
    'activity:customers.GOOGLE_AUTHENTICATOR',
  'authenticator:duo_security': 'activity:customers.DUO_SECURITY',

  // ========================================
  // Protocols & Applications
  // ========================================
  rdp: 'activity:features.RDP',
  http: 'activity:features.HTTP',
  https: 'activity:features.HTTPS',
  ssh: 'activity:features.SSH',
  vnc: 'activity:features.VNC',

  // ========================================
  // Platform Features
  // ========================================
  ucpUsage: 'activity:customers.UCP_USAGE',
  isNprUsing: 'activity:about.FIREWALL',
  isDpcUsing: 'activity:customers.DPC',
  isUsingSwg: 'activity:platform.WEB_FILTERING_RULES',
  isUsingBypass: 'activity:platform.BYPASS_RULES',
  isUsingExternalApi: 'activity:customers.EXTERNAL_API',
  isUsingMonitoringDashboard: 'activity:customers.MONITORING_DASHBOARD',

  // ========================================
  // Category Labels
  // ========================================
  cat_idp: 'activity:customers.IDENTITY_PROVIDERS',
  cat_tfa: 'activity:customers.TWO_FACTOR_AUTHENTIFICATION',
  cat_nf: 'activity:customers.NETWORK_FEATURES',
  cat_clients: 'activity:customers.AGENT_AND_PLATFORM_CONFIG',
  cat_app: 'activity:platform.APPLICATIONS',
  cat_sf: 'activity:customers.SECURITY_FEATURES',
  cat_siem: 'activity:customers.SIEM_INTEGRATION',
  cat_dashboards: 'activity:customers.DASHBOARDS'
} as const;

/**
 * Get all feature keys
 */
export const getFeatureKeys = (): string[] =>
  Object.keys(FEATURE_TRANSLATION_MAP);

/**
 * Check if a feature key exists in the mapping
 */
export const hasFeatureKey = (key: string): boolean =>
  key in FEATURE_TRANSLATION_MAP;
